import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useSearchParams, useBlocker, type BlockerFunction } from 'react-router'
import { toast } from 'sonner'
import { apiClient, useAuthStore, getSiteColor } from '@indipia/shared'
import { CommonCodesProvider } from '../context/CommonCodesContext'
import { SiteRefreshProvider } from '../context/SiteRefreshContext'
import { CalendarMultiView } from '../views/CalendarMultiView'
import type { SiteInfo } from '../views/CalendarMultiView'
import { SiteSelectView } from '../views/SiteSelectView'
import { UploadView } from '../views/UploadView'
import type { ExtractResult } from '../views/extractTypes'
import { ExtractView, type ExtractInputSnapshot } from '../views/ExtractView'
import type { OriginType, SavePayload } from '../hooks/useScheduleSave'
// 자동 생성(QUOTE) 흐름: 현장 선택(create-site) → 업로드(create-upload2) → 추출 결과 확인(create-extract-raw2)
// → 추출 결과 확인에서 공종·공사 기간을 정하고 [스케줄 생성하기]로 바로 생성 → 간트. 이동 규칙 정본 = V2/1019/작업기록/F1_이동규칙.md
// 공종·공사일 전용 화면(create-extract2)·배정 확인(assign-preview)은 이 흐름에서 거치지 않는다(직접 생성은 그대로 create-extract → 배정 확인).
import { UploadView2 } from '../views/UploadView2'
import { AssignPreviewView, type PreviewAssignResult } from '../views/AssignPreviewView'
import { ExtractRawView2, EMPTY_RAW_CHOICE, type RawExtract, type RawChoice } from '../views/ExtractRawView2'
import { GanttView, type GanttDeepLink } from '../views/GanttView'
import { DEEP_LINK_KEYS, parseDeepLink, readPositiveId } from '../constants/deepLink'
import { CalendarScheduleView } from '../views/CalendarScheduleView'
import { SiteListView } from '../views/SiteListView'
import { useCompanyMembers, useDefaultMemberFilter, useMemberFilterState, type AssignedMember } from '@indipia/shared-features'
import { KakaoShareView } from '../views/KakaoShareView'
import { ScheduleListView } from '../views/ScheduleListView'
import { SettingsView } from '../views/SettingsView'
import { DurationSettingsView } from '../views/DurationSettingsView'
import { OrderSettingsView } from '../views/OrderSettingsView'
import { ForemanConfirmSettingsView } from '../views/ForemanConfirmSettingsView'
import { PartnerAssignView } from '../views/PartnerAssignView'
import { ShareHistoryView } from '../views/ShareHistoryView'
import { useShareSendableMap } from '../hooks/useShareSendable'
import { ScheduleChangeNoticeModal } from '../components/ScheduleChangeNoticeModal'
import type { DateChange } from '../api/foremanApi'
import { Button, Modal } from '@indipia/ui-core'
import { RightPanel, SCHED_MGMT_VIEWS, CAL_HOME_STATUS_ORDER, SCHED_PANEL_STATUS_ORDER } from '../components/SchedulerRightPanel'

export type WorkDateResult = {
  startDate: string
  endDate: string
  works: { workTypeCode: string; duration: number }[]
}

// 스케줄/캘린더/리스트(간트·달력·목록 뷰)가 저장 안 한 변경사항이 있을 때 이걸 부모에
// 보고한다. 부모는 다른 현장 클릭·다른 페이지 이동 시 이걸로 저장 확인창을 띄운다.
// 저장 안 한 변경사항 확인 모달 본문 글자 스타일(상세창·날짜·설정 세 경우가 같이 씀)
const DIRTY_BODY_STYLE: React.CSSProperties = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', lineHeight: 1.6 }

export interface DirtyHandle {
  count: number
  /** 이탈 확인 문구의 대상 이름 — 없으면 '날짜가 변경된 공종'(간트·캘린더·리스트). 발주 설정 화면은 '바꾼 공종 설정' */
  subject?: string
  /** 공종 상세창에 저장 안 한 편집이 있음 — 이탈 확인 문구가 달라지고 [저장] 없이 이동/계속 편집만 묻는다 */
  panel?: boolean
  /** 저장 결과로 변경 카톡 확인 대상이 있으면 돌려준다(FR-C-120) — 이동 뒤에도 부모가 확인창을 띄운다. */
  save: () => Promise<DirtySaveNotice | null | void>
  discard: () => void
}

/** 날짜 저장 응답의 변경 카톡 확인 대상(서버 notifiable 항목) */
export interface DirtySaveNotice {
  scheduleId: number
  changes: DateChange[]
}

export type View =
  | 'calendar'
  | 'gantt'
  | 'cal-b2'
  | 'site-list'
  | 'create-site'
  | 'create-upload'
  | 'create-extract'
  // 자동 생성(QUOTE) 전용 단계 — 업로드 → 추출 결과 확인 → 공종·공사일
  | 'create-upload2'
  | 'create-extract-raw2'
  | 'create-extract2'
  | 'assign-preview'
  | 'kakao'
  | 'kakao-history'
  | 'work-type'
  | 'settings'
  | 'settings-alarm'
  | 'settings-duration'
  | 'settings-order'
  | 'settings-partner'
  | 'settings-foreman-confirm'

/** 스케줄 생성 흐름에 속한 단계인지(현장 선택~배정 확인) */
const isCreateFlowView = (v: View): boolean => v.startsWith('create-') || v === 'assign-preview'

/**
 * 화면 이동. taskId 는 'gantt' 에서 상세창을 열 공종(숫자 PK).
 * card 는 'gantt' + taskId 일 때만 의미가 있다(상세창 카드: pt·sup, bar=막대 강조).
 */
export type NavFn =(v: View, scheduleId?: number, taskId?: number, initMonth?: { year: number; month: number }, resetSiteId?: number, card?: GanttDeepLink['card']) => void


// ── API 타입 ─────────────────────────────────────────────────────────────────
interface ScheduleSummary {
  scheduleId:     number
  siteId:         number
  siteName:       string
  customerName:   string
  contact:        string
  startDate:      string
  endDate:        string
  scheduleStatus: string
  siteDoneYn?:    string
  siteColor:      string | null
  chargeMembers?:  AssignedMember[]
  managerMembers?: AssignedMember[]
}

function toSiteInfo(s: ScheduleSummary): SiteInfo {
  return {
    scheduleId:     s.scheduleId,
    siteId:         s.siteId,
    siteName:       s.siteName       ?? '',
    customerName:   s.customerName   ?? '',
    contact:        s.contact        ?? '',
    startDate:      s.startDate      ?? '',
    endDate:        s.endDate        ?? '',
    scheduleStatus: s.scheduleStatus ?? '',
    siteDoneYn:     s.siteDoneYn,
    color:          s.siteColor ?? getSiteColor(s.siteId),
    taskCount:      0,
    progress:       0,
    chargeMembers:  s.chargeMembers  ?? [],
    managerMembers: s.managerMembers ?? [],
  }
}

// ── 메인 페이지 ──────────────────────────────────────────────────────────────
export function SchedulerPage() {
  const user = useAuthStore(s => s.user)

  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<View>(
    () => (searchParams.get('view') as View) ?? 'calendar'
  )
  // 스튜디오 홈 "할 일" 카드에서 넘어온 진입모드(entry=QUOTE/MANUAL) — navigate()가 매번
  // searchParams를 {view}로 통째로 새로 쓰기 때문에 entry는 URL에 계속 남지 않는다.
  // 그래서 상태로 들고 가되, 사이드바 메뉴 등 외부에서 URL이 바뀌어 들어오면(내부 navigate 가 쓴
  // URL이 아니면) 아래 URL 동기 effect 에서 URL 의 entry 로 다시 맞춘다(재마운트 여부와 무관하게 stale 방지).
  const [entryMode, setEntryMode] = useState<string | null>(() => searchParams.get('entry'))
  // 생성 경로 — 진입 시점에 확정해 흐름 끝까지 전달한다(QUOTE=견적서, MANUAL=직접. 진입 값이 없으면 '생성 방법 선택'에서 사용자가 고른 시점에 확정).
  const originFromEntry = (e: string | null): OriginType | null => (e === 'QUOTE' ? 'QUOTE' : e === 'MANUAL' ? 'MANUAL' : null)
  // 상태로 보존한다 — 단계 이동으로 URL 이 {view} 로 바뀌거나 entry 가 사라져도 저장 단계(originType)가 막히지 않게 하는 정본.
  // 갱신 지점: ① 외부 진입 URL 의 entry ② QUOTE 전용 단계(create-upload2·create-extract-raw2·create-extract2)로 이동 ③ 생성 방법 선택에서 직접 입력 선택.
  const [createOrigin, setCreateOrigin] = useState<OriginType | null>(() => originFromEntry(searchParams.get('entry')))
  // navigate() 가 직접 쓴 URL(search 문자열) — 이 값과 같은 URL 변화는 내부 이동이라 entry 를 건드리지 않는다.
  const internalSearchRef = useRef<string | null>(null)

  // E.나의 현장 "공사스케줄" 태그(/agents/scheduler?view=gantt&siteId=354)로 특정 현장만 곧장
  // 보여주기 위한 진입 파라미터 — entryMode와 동일하게 최초 마운트 시 1회만 읽는다(이후
  // 사용자가 다른 현장으로 전환해도 이 값으로 되돌아가면 안 되므로 로컬 상태로 고정).
  const [initialSiteId] = useState<number | null>(() => readPositiveId(searchParams, 'siteId'))

  // 스케줄 관리(스케줄/캘린더/리스트 + 하위 마일스톤/공종수정) 밖에서 안으로 들어올 때
  // 활성 현장(schedActiveSiteId)을 리셋하기 위한 추적용 ref — 우측 패널에서 특정 현장을
  // 켰던 상태가 다른 메뉴를 거쳐 돌아왔을 때도 "맨 위 고정"으로 남아있는 문제를 막는다.
  // view state를 바꾸는 것과 같은 동기 구간에서 리셋해야 한다: 별도 useEffect로 한 박자
  // 늦게 리셋하면, 우측 패널 정렬(RightPanel의 sortedSites)이 sites 배열 참조가 바뀌는
  // 시점(= view 전환 렌더)에 이미 "이전 활성 현장 맨 위"로 한 번 계산되어 고정돼버려서
  // (카드가 안 튀도록 일부러 재정렬을 억제하는 설계) 이후에 리셋해도 반영되지 않는다.
  const wasInSchedMgmtRef = useRef(SCHED_MGMT_VIEWS.has(view))
  const shouldResetSchedActive = (newView: View): boolean => {
    const inSchedMgmt = SCHED_MGMT_VIEWS.has(newView)
    const was = wasInSchedMgmtRef.current
    wasInSchedMgmtRef.current = inSchedMgmt
    return inSchedMgmt && !was
  }

  const navigate: NavFn = (target: View, scheduleId?: number, taskId?: number, initMonth?: { year: number; month: number }, resetSiteIdParam?: number, card?: GanttDeepLink['card']) => {
    let v: View = target
    // ── 생성 흐름 이동 규칙 ───────────────────────────────────────────────────
    // 현장 선택(SiteSelectView)은 직접·견적 공용이라 [다음]을 항상 'create-upload' 로 호출한다.
    // 견적 진입(QUOTE)이면 생성 방법 선택을 거치지 않고 업로드(AI 분석)로 보낸다.
    // 정본: V2/1019/작업기록/F1_이동규칙.md
    if (v === 'create-upload' && view === 'create-site' && createOrigin === 'QUOTE' && resetSiteIdParam === undefined) {
      v = 'create-upload2'
    }
    // 견적 전용 단계에 들어가면 생성 경로를 QUOTE 로 확정해 둔다(URL entry 와 무관하게 보존)
    const nextOrigin: OriginType | null =
      v === 'create-upload2' || v === 'create-extract-raw2' || v === 'create-extract2' ? 'QUOTE' : createOrigin
    if (nextOrigin !== createOrigin) setCreateOrigin(nextOrigin)

    if (shouldResetSchedActive(v)) setSchedActiveSiteId(null)
    if (scheduleId !== undefined) setSchedActiveSiteId(scheduleId)
    // 리스트 → 간트 상세창(E-02): 공종 번호가 있으면 활성 현장의 siteId 로 딥링크를 만든다(주소를 거치지 않음).
    // 현장을 찾지 못하면 딥링크 없이 간트 기본 화면으로만 이동한다(임의 현장으로 대체하지 않음).
    if (v === 'gantt' && taskId !== undefined) {
      const targetScheduleId = scheduleId ?? schedActiveSiteId
      const targetSite = knownSites.find(s => s.scheduleId === targetScheduleId)
      if (targetSite) setDeepLink({ siteId: targetSite.siteId, taskId, card: card ?? null, pop: null })
    }
    // 스케줄 생성 흐름 진입점은 둘 — (1) 'create-site'로 새로 시작(현장 선택부터, 3단계)
    // (2) 'create-upload'에 resetSiteIdParam을 들고 바로 진입(초기화 흐름, 현장 선택 건너뛰고 2단계).
    // 두 진입점에서만 상태를 리셋/설정한다 — 단계 사이 이동(create-site→create-upload 등)에서는
    // 이미 저장해둔 selectedSiteId/createInitMonth를 건드리지 않는다.
    // 생성 흐름 안에서 돌아오는 경우(공종 선택의 [이전] 등)는 선택한 현장·입력을 유지한다.
    const inCreateFlow = view.startsWith('create-') || view === 'assign-preview'
    // 내부 버튼(홈 캘린더·간트 초기화면 등)에서 새로 시작하면 이전 진입값(entry)·생성 경로·추출 결과도 비운다
    // — 진입값은 외부 URL(스튜디오 홈 카드)로만 들어오므로, 내부 시작은 '기타 경로'(생성 방법 선택 경유)다.
    let originNow = nextOrigin
    if (v === 'create-site' && resetSiteIdParam === undefined && !inCreateFlow) {
      setCreateInitMonth(initMonth ?? null)
      setResetSiteId(null)
      setSelectedSiteId(null)
      setEntryMode(null)
      setCreateOrigin(null)
      originNow = null
      setExtractResult(null)
      setRawExtract(null)
      setCreateDraft(null)
      setCreatePayload(null)
      setPreviewAssignResult(null)
    }
    if (v === 'create-upload' && resetSiteIdParam !== undefined) {
      setCreateInitMonth(initMonth ?? null)
      setResetSiteId(resetSiteIdParam)
      setSelectedSiteId(null)
      setEntryMode(null)
      setCreateOrigin(null)
      originNow = null
      setExtractResult(null)
      setRawExtract(null)
      setCreateDraft(null)
      setCreatePayload(null)
      setPreviewAssignResult(null)
    }
    // 스튜디오 홈 "견적서 없이 스케줄을 직접 만들어요"(entry=MANUAL)로 들어온 경우,
    // 현장 선택 다음의 '생성 방법 선택'(create-upload) 화면을 건너뛰고 공종/공사기간
    // 선택(create-extract)으로 바로 진입한다 — UploadView의 handleDirectInput과 동일한 결과.
    if (v === 'create-upload' && entryMode === 'MANUAL' && resetSiteIdParam === undefined) {
      setExtractResult({ works: [], skipped: true })
      v = 'create-extract'
    }
    setView(v)
    // 생성 흐름 안의 이동은 URL 에도 생성 경로(entry)를 남겨 새로고침 시 같은 경로로 현장 선택부터 이어지게 한다.
    // (상태가 정본이고 URL 은 새로고침 복원용 — 생성 흐름 밖으로 나가면 {view} 만 쓴다)
    const nextParams = new URLSearchParams({ view: v })
    if (isCreateFlowView(v) && originNow) nextParams.set('entry', originNow)
    internalSearchRef.current = nextParams.toString()
    setSearchParams(nextParams, { replace: true })
  }

  // 공통 플랫폼 nav에서 URL ?view= 변경 시 내부 view 동기화(사이드바 메뉴 클릭 등)
  useEffect(() => {
    let urlView = (searchParams.get('view') as View) ?? 'calendar'
    const isInternal = internalSearchRef.current === searchParams.toString()
    internalSearchRef.current = null
    if (!isInternal) {
      // 외부(메뉴 클릭·직접 URL·새로고침)에서 들어옴 — entry 를 URL 기준으로 갱신한다.
      // 치환 없음: view=create-site&entry=QUOTE 는 현장 선택에서 시작한다.
      const urlEntry = searchParams.get('entry')
      setEntryMode(urlEntry)
      setCreateOrigin(originFromEntry(urlEntry))
      // 외부에서 생성 흐름에 새로 진입하면 이전 입력(현장·추출 결과·공종 입력)은 버린다
      if (isCreateFlowView(urlView)) {
        setCreateInitMonth(null)
        setResetSiteId(null)
        setSelectedSiteId(null)
        setExtractResult(null)
        setRawExtract(null)
        setCreateDraft(null)
        setCreatePayload(null)
        setPreviewAssignResult(null)
      }
    }
    if (shouldResetSchedActive(urlView)) setSchedActiveSiteId(null)
    setView(urlView)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const [calSites,           setCalSites]           = useState<SiteInfo[]>([])
  const [allSites,           setAllSites]           = useState<SiteInfo[]>([])
  const [allSitesLoading,   setAllSitesLoading]     = useState(true)
  const [allSitesLoadError, setAllSitesLoadError]   = useState(false)
  const now = new Date()
  const [calYear,  setCalYear]  = useState(now.getFullYear())
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1)
  const handleCalMonthChange = useCallback((y: number, m: number) => { setCalYear(y); setCalMonth(m) }, [])
  const [calInactiveSiteIds, setCalInactiveSiteIds] = useState<Set<number>>(new Set())
  const [calJumpTo,          setCalJumpTo]          = useState<{ startDate: string; endDate: string } | null>(null)
  const [schedActiveSiteId,  setSchedActiveSiteId]  = useState<number | null>(null)

  const [extractResult,      setExtractResult]      = useState<ExtractResult | null>(null)
  // [CUSTOM2 파일럿] LLM raw 추출 결과 — 임시 추출결과 페이지(create-extract-raw2)에서 나열
  const [rawExtract,         setRawExtract]         = useState<RawExtract | null>(null)
  // 추출 결과 확인 화면에서 사용자가 고른 값(미매칭 공종 선택·공통 항목 체크) — 분석 결과가 바뀌면 비운다
  const [rawChoice,          setRawChoice]          = useState<RawChoice>(EMPTY_RAW_CHOICE)
  useEffect(() => { setRawChoice(EMPTY_RAW_CHOICE) }, [rawExtract])
  // 업로드한 견적서 원본 — 공종표의 '원문 보기'용. 분석 결과와 함께 보관하고 결과가 비워지면(새 파일·현장 변경·이탈) 같이 버린다
  const [rawFile,            setRawFile]            = useState<File | null>(null)
  useEffect(() => { if (!rawExtract) setRawFile(null) }, [rawExtract])
  // 공종 선택 화면 입력(이전·배정 확인 왕복 시 유지, 이탈 경고 기준) — 생성 흐름을 벗어나면 비운다.
  const [createDraft, setCreateDraft] = useState<ExtractInputSnapshot | null>(null)
  // 자동 배정 켬 → 배정 확인 화면: 미리보기 결과 + 저장에 쓸 값
  const [previewAssignResult, setPreviewAssignResult] = useState<PreviewAssignResult | null>(null)
  const [createPayload, setCreatePayload] = useState<SavePayload | null>(null)
  const [createInitMonth,    setCreateInitMonth]    = useState<{ year: number; month: number } | null>(null)
  const [resetSiteId,        setResetSiteId]        = useState<number | null>(null)
  // 1단계(현장 선택)에서 고른 현장 — 마지막 단계(공종/공사일 선택)의 스케줄 생성 호출까지 들고 간다
  const [selectedSiteId,     setSelectedSiteId]     = useState<number | null>(null)
  const [sitesRefreshKey,    setSitesRefreshKey]    = useState(0)
  const [ganttTaskPanelOpen, setGanttTaskPanelOpen] = useState(false)

  useEffect(() => {
    // compId 가 없으면 조회 자체를 못 한다 — 로딩을 내려 스켈레톤이 남지 않게 한다
    if (!user?.compId) { setAllSitesLoading(false); return }
    setAllSitesLoadError(false)
    setAllSitesLoading(true)
    const headers = { 'X-Comp-Id': String(user.compId) }
    apiClient
      .get<{ data: { schedules: ScheduleSummary[] } }>('/schedule/schedules', {
        params: { pageSize: 200 },
        headers,
      })
      .then(res => {
        setAllSites((res.data.data?.schedules ?? []).map(toSiteInfo))
      })
      .catch(() => setAllSitesLoadError(true))
      .finally(() => setAllSitesLoading(false))
  }, [user?.compId, sitesRefreshKey])

  // 담당자·관리자 조건(v1.9) — 우측 현장 목록 6개 화면 공통. 기본값: 직원은 나, 사장님은 조건 없음(전체 현장).
  // 조건이 있으면 서버(S-1 memberIds)가 거른 스케줄 목록을 우측 패널에 그대로 그린다. 조건 없는 allSites(200건)와
  // 겹치지 않으므로 200건 밖의 담당 현장도 빠지지 않는다. 모든 일정 확인(캘린더)은 본문 · 오늘 할일도 우측 목록에서
  // 켜져 있는 현장만 보여준다(calVisibleSchedIds). 간트 등 단일 현장 화면 본문은 거르지 않는다.
  const { myId } = useDefaultMemberFilter()
  const { data: companyMembers = [] } = useCompanyMembers()
  // 로그인 정보가 늦게 들어와도 직원은 [나], 사장님은 [] 로 맞춘다
  const [memberIds, setMemberIds] = useMemberFilterState()
  const memberIdsKey = memberIds.join(',')
  // null = 조건 없음(전체). 조건이 있는데 아직 조회 전이면 undefined
  const [scopeSites, setScopeSites] = useState<SiteInfo[] | null | undefined>(memberIdsKey ? undefined : null)
  const [scopeLoadError, setScopeLoadError] = useState(false)

  useEffect(() => {
    if (!memberIdsKey) { setScopeSites(null); setScopeLoadError(false); return }
    if (!user?.compId) return
    setScopeSites(undefined)
    setScopeLoadError(false)
    const controller = new AbortController()
    apiClient
      .get<{ data: { schedules: ScheduleSummary[] } }>('/schedule/schedules', {
        // List<Long> 는 CSV 로 받는다 — axios 기본 배열 직렬화(memberIds[]=)는 바인딩되지 않는다
        params: { pageSize: 200, memberIds: memberIdsKey },
        headers: { 'X-Comp-Id': String(user.compId) },
        signal: controller.signal,
      })
      .then(res => setScopeSites((res.data.data?.schedules ?? []).map(toSiteInfo)))
      .catch((err: { name?: string }) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return
        setScopeSites([])
        setScopeLoadError(true)
      })
    return () => controller.abort()
  }, [user?.compId, memberIdsKey, sitesRefreshKey])

  const knownSites = useMemo(() => {
    if (!scopeSites?.length) return allSites
    const ids = new Set(allSites.map(s => s.scheduleId))
    return [...allSites, ...scopeSites.filter(s => !ids.has(s.scheduleId))]
  }, [allSites, scopeSites])

  // 비캘린더 뷰에서 현장 미선택이면 자동 선택 — siteId 진입 파라미터가 있으면 그 현장의
  // scheduleId를 우선 매칭하고(E.나의 현장 "공사스케줄" 태그), 없거나 못 찾으면 첫 번째 현장.
  // 담당자·관리자 조건이 있으면 조건에 맞는 첫 현장을 고른다(조건 조회 중이면 기다린다).
  useEffect(() => {
    if (view !== 'calendar' && schedActiveSiteId === null && knownSites.length > 0) {
      const matched = initialSiteId !== null ? knownSites.find(s => s.siteId === initialSiteId) : undefined
      if (matched) { setSchedActiveSiteId(matched.scheduleId); return }
      if (scopeSites === undefined) return
      const first = scopeSites ? scopeSites[0] : allSites[0]
      if (first) setSchedActiveSiteId(first.scheduleId)
    }
  }, [allSites, knownSites, schedActiveSiteId, view, initialSiteId, scopeSites])

  // 이미 스케줄러 화면에 있는 상태에서 알림 클릭 등으로 siteId 파라미터만 바뀌어 들어오면
  // 그 현장을 선택한다(최초 진입분은 위 자동 선택이 처리하므로 마지막 처리값으로 시작).
  const appliedSiteParam = useRef<number | null>(initialSiteId)
  useEffect(() => {
    const n = readPositiveId(searchParams, 'siteId')
    if (n === null || n === appliedSiteParam.current) return
    const matched = knownSites.find(s => s.siteId === n)
    if (!matched) return
    appliedSiteParam.current = n
    setSchedActiveSiteId(matched.scheduleId)
  }, [searchParams, knownSites])

  // ── 딥링크(주소 컨텍스트) — siteId·taskId·card·pop (나의 할일·알림 등에서 진입) ─────────────
  // 한 번 읽은 뒤 주소에서 제거한다(view·entry 등 나머지는 유지). 모르는 값은 무시한다.
  // taskId·card·pop 은 view=gantt 에서만 의미가 있고 siteId 가 함께 있을 때만 간트로 넘긴다.
  const [deepLink, setDeepLink] = useState<GanttDeepLink | null>(null)
  const [siteCheckId, setSiteCheckId] = useState<number | null>(null)
  useEffect(() => {
    if (!DEEP_LINK_KEYS.some(k => searchParams.has(k))) return
    const { siteId, taskId, card, pop } = parseDeepLink(searchParams)
    const urlView = searchParams.get('view') ?? 'calendar'
    if (siteId !== null && urlView !== 'calendar') {
      setSiteCheckId(siteId)
      if (urlView === 'gantt' && (taskId !== null || pop !== null)) {
        setDeepLink({ siteId, taskId, card, pop })
      }
    }
    const next = new URLSearchParams(searchParams)
    DEEP_LINK_KEYS.forEach(k => next.delete(k))
    internalSearchRef.current = next.toString()
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])
  // 현장 목록 조회가 끝난 뒤 그 현장을 열 수 없으면(권한 없음·삭제됨) 안내하고 딥링크를 버린다.
  useEffect(() => {
    if (siteCheckId === null || allSitesLoading || scopeSites === undefined) return
    if (!allSitesLoadError && !knownSites.some(s => s.siteId === siteCheckId)) {
      toast.error('이 현장을 열 수 없어요(권한이 없거나 삭제된 현장).')
      setDeepLink(null)
    }
    setSiteCheckId(null)
  }, [siteCheckId, allSitesLoading, allSitesLoadError, scopeSites, knownSites])
  useEffect(() => { if (view !== 'gantt' && deepLink) setDeepLink(null) }, [view, deepLink])

  const handleScheduleCreated = useCallback((scheduleId: number) => {
    setSitesRefreshKey(k => k + 1)
    setSchedActiveSiteId(scheduleId)
    setResetSiteId(null)
    setSelectedSiteId(null)
    setCreateDraft(null)
    setCreatePayload(null)
    setPreviewAssignResult(null)
    // "스케줄 생성" 완료 직후 스케줄 화면으로 넘어오는 흐름은 리셋 예외 —
    // 이미 방금 만든 현장을 명시적으로 활성화했으므로, 아래 shouldResetSchedActive의
    // 일반 리셋 로직이 다시 끼어들지 않도록 "이미 스케줄 관리 안에 있다"로 표시해둔다.
    wasInSchedMgmtRef.current = true
    setView('gantt')
    setSearchParams({ view: 'gantt' }, { replace: true })
  }, [setSearchParams])

  // 공종 선택 → 배정 확인으로 넘어갈 때(자동 배정 켬)
  const handleCreatePreview = (r: PreviewAssignResult, payload: SavePayload) => {
    setPreviewAssignResult(r)
    setCreatePayload(payload)
    navigate('assign-preview')
  }

  // [테스트 전용] 현장 선택 화면의 바로가기 — 업로드·분석 없이 샘플 추출 결과로 추출 결과 확인 화면에 진입한다.
  const handleDevSample = (siteId: number, raw: RawExtract) => {
    setSelectedSiteId(siteId)
    setRawExtract(raw)
    setExtractResult({
      works: raw.standardWorks.filter(w => w.matchedCode).map(w => ({ workTypeCode: w.matchedCode!, workTypeName: w.matchedName ?? '', duration: w.duration ?? 1 })),
      skipped: false,
    })
    setCreateDraft(null)
    navigate('create-extract-raw2')
  }

  // 생성 흐름을 벗어나면 보관한 입력을 비운다
  useEffect(() => {
    if (view.startsWith('create-') || view === 'assign-preview') return
    setCreateDraft(null)
    setCreatePayload(null)
  }, [view])

  // 현장 선택 화면의 [다음] — 선택한 현장이 바뀌면 그 전에 받은 추출 결과·공종 입력은 버린다(현장 → 업로드 순서라 바뀐 현장 기준으로 다시 진행)
  const handleSiteSelected = useCallback((siteId: number | null) => {
    if (siteId !== selectedSiteId) {
      setRawExtract(null)
      setExtractResult(null)
      setCreateDraft(null)
      setCreatePayload(null)
      setPreviewAssignResult(null)
    }
    setSelectedSiteId(siteId)
  }, [selectedSiteId])

  // 새로고침·직접 URL 진입으로 선행 데이터 없이 자동 생성(QUOTE) 중간 단계에 도달하면 빈 화면 대신 앞 단계로 돌려보낸다.
  //  ① 현장이 없으면 → 현장 선택  ② 견적서 추출 결과가 없으면 → 업로드 (순서 = 현장 → 업로드 → 추출 결과 → 공종·공사일)
  useEffect(() => {
    const quoteStep = view === 'create-upload2' || view === 'create-extract-raw2' || view === 'create-extract2'
    if (!quoteStep) return
    if ((resetSiteId ?? selectedSiteId) == null) {
      toast.info('현장 정보가 없어 현장 선택 화면으로 돌아왔어요')
      navigate('create-site')
      return
    }
    if (view === 'create-upload2') return
    if (rawExtract && (view !== 'create-extract2' || extractResult)) return
    toast.info('견적서 분석 결과가 없어 업로드 단계로 돌아왔어요. 견적서를 다시 올려 주세요')
    navigate('create-upload2')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, rawExtract, extractResult, selectedSiteId, resetSiteId])

  // 공종 선택·배정 확인 중 입력을 시작했으면 새로고침·탭 닫기 전에 확인
  const createLeaveGuard = !!createDraft?.touched && (view === 'create-extract' || view === 'create-extract2' || view === 'assign-preview')
  useEffect(() => {
    if (!createLeaveGuard) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [createLeaveGuard])

  const isCalendarView = view === 'calendar'

  const calActiveSiteIds = useMemo(
    () => new Set(calSites.map(s => s.scheduleId)),
    [calSites]
  )

  const calSiteColorMap = useMemo(
    () => new Map(calSites.map(s => [s.scheduleId, s.color])),
    [calSites]
  )

  // 인디플래너 홈(캘린더)에서 보고 있는 달과 현장의 공사기간이 겹치는지
  const isInCalMonth = useCallback((s: SiteInfo) => {
    if (!s.startDate || !s.endDate) return false
    const monthStart = `${calYear}-${String(calMonth).padStart(2, '0')}-01`
    const lastDay = new Date(calYear, calMonth, 0).getDate()
    const monthEnd = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    return s.startDate <= monthEnd && s.endDate >= monthStart
  }, [calYear, calMonth])

  // 스케줄 공유 화면 전용 — 서버 shareSendable(종료일+7일 이내). 날짜는 화면에서 계산하지 않는다.
  const shareSendableMap = useShareSendableMap(view === 'kakao', sitesRefreshKey)

  // 담당자·관리자 조건 — 조건이 있으면 서버가 거른 목록이 출발점(조건 밖 현장은 보고 있어도 카드에서 빠진다)
  const panelSites = useMemo(() => {
    if (scopeSites === undefined) return []
    const baseSites = scopeSites ?? allSites
    // 시공완료 현장은 우측 패널(스케줄/캘린더/리스트/인디플래너 홈 공통)에 노출하지 않는다.
    // 단 스케줄 공유 화면은 시공완료여도 서버가 발송 가능(종료일+7일 이내)이라 한 현장은 노출한다.
    const notDone = baseSites.filter(s => {
      const st = (s.scheduleStatus ?? '').toUpperCase()
      if (st !== 'DONE' && st !== 'COMPLETED') return true
      return view === 'kakao' && shareSendableMap?.get(s.scheduleId) === true
    })
    if (!isCalendarView) return notDone
    return notDone
      .filter(isInCalMonth)
      .sort((a, b) => {
        const aActive = calActiveSiteIds.has(a.scheduleId) ? 0 : 1
        const bActive = calActiveSiteIds.has(b.scheduleId) ? 0 : 1
        return aActive - bActive
      })
      .map(s => {
        const calColor = calSiteColorMap.get(s.scheduleId)
        return calColor ? { ...s, color: calColor } : s
      })
  }, [isCalendarView, view, shareSendableMap, allSites, scopeSites, calActiveSiteIds, calSiteColorMap, isInCalMonth])

  // 모든 일정 확인 — 우측 목록(담당자·관리자 조건 · 해당 달 · 시공완료 제외)에 있고 사용자가 끄지 않은 현장만 켜진 현장.
  // 현장 목록 조회 중이면 null(거르지 않음) — 조회가 끝나기 전 캘린더 · 오늘 할일이 잠깐 비지 않게.
  const calVisibleSites = useMemo(() => {
    if (!isCalendarView || allSitesLoading || scopeSites === undefined) return null
    return panelSites.filter(s => !calInactiveSiteIds.has(s.scheduleId))
  }, [isCalendarView, allSitesLoading, scopeSites, panelSites, calInactiveSiteIds])
  const calVisibleSchedIds = useMemo(
    () => calVisibleSites ? new Set(calVisibleSites.map(s => s.scheduleId)) : null,
    [calVisibleSites],
  )
  // 오늘 할일은 현장 ID 로 온다 — 켜진 현장의 siteId
  const calVisibleSiteIds = useMemo(
    () => calVisibleSites ? new Set(calVisibleSites.map(s => s.siteId)) : null,
    [calVisibleSites],
  )

  const inactiveSiteIds = useMemo(() => {
    if (isCalendarView) {
      const result = new Set<number>()
      const all = [...knownSites, ...calSites]
      all.forEach(s => {
        if (!calActiveSiteIds.has(s.scheduleId) || calInactiveSiteIds.has(s.scheduleId)
          || (calVisibleSchedIds && !calVisibleSchedIds.has(s.scheduleId)))
          result.add(s.scheduleId)
      })
      return result
    }
    if (schedActiveSiteId === null) return new Set(knownSites.map(s => s.scheduleId))
    return new Set(knownSites.map(s => s.scheduleId).filter(id => id !== schedActiveSiteId))
  }, [isCalendarView, calInactiveSiteIds, calActiveSiteIds, calVisibleSchedIds, calSites, schedActiveSiteId, knownSites])

  const handleSitesLoaded = useCallback((sites: SiteInfo[]) => {
    setCalSites(sites)
    setCalInactiveSiteIds(new Set())
  }, [])

  // ── 저장 안 한 변경사항 가드 ──────────────────────────────────
  // 스케줄/캘린더/리스트(간트·달력·목록)에 저장 안 한 날짜 변경이 있을 때, 다른 현장을
  // 클릭하거나(= 라우팅 없는 순수 상태 변경) 다른 페이지로 이동(= URL 변경)하면 저장할지
  // 물어본다. 페이지 이동은 useBlocker(react-router)로 한 번에 잡고, 현장 클릭은 라우팅이
  // 없는 별개의 상태 변경이라 guardedAction으로 따로 감싼다.
  const dirtyRef = useRef<DirtyHandle | null>(null)
  const setDirty = useCallback((h: DirtyHandle | null) => { dirtyRef.current = h }, [])
  type DirtyPrompt = { kind: 'route' } | { kind: 'action'; run: () => void }
  const [dirtyPrompt, setDirtyPrompt] = useState<DirtyPrompt | null>(null)
  const [dirtySaving, setDirtySaving] = useState(false)
  // "저장하고 이동"으로 날짜를 저장했을 때의 변경 카톡 확인 대상 — 이동하면 화면(뷰)이 사라지므로 부모가 들고 있다가 띄운다(FR-C-120).
  const [leaveNotice, setLeaveNotice] = useState<DirtySaveNotice | null>(null)

  const shouldBlockNav = useCallback<BlockerFunction>(({ currentLocation, nextLocation }) =>
    dirtyRef.current !== null &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  [])
  const blocker = useBlocker(shouldBlockNav)
  useEffect(() => {
    if (blocker.state === 'blocked') setDirtyPrompt({ kind: 'route' })
  }, [blocker.state])

  const guardedAction = useCallback((action: () => void) => {
    if (dirtyRef.current) setDirtyPrompt({ kind: 'action', run: action })
    else action()
  }, [])

  const proceedDirtyPrompt = () => {
    if (dirtyPrompt?.kind === 'route') blocker.proceed?.()
    else if (dirtyPrompt?.kind === 'action') dirtyPrompt.run()
    setDirtyPrompt(null)
  }
  const handleDirtySave = async () => {
    if (!dirtyRef.current) { proceedDirtyPrompt(); return }
    setDirtySaving(true)
    try {
      const notice = await dirtyRef.current.save()
      dirtyRef.current = null
      if (notice && notice.changes.length > 0) setLeaveNotice(notice)
      proceedDirtyPrompt()
    } catch {
      // 저장 실패: 각 뷰가 오류를 알렸으므로 변경 상태를 유지하고 이동하지 않는다(DF-50)
    } finally {
      setDirtySaving(false)
    }
  }
  const handleDirtyDiscard = () => {
    dirtyRef.current?.discard()
    dirtyRef.current = null
    proceedDirtyPrompt()
  }
  const handleDirtyCancel = () => {
    if (dirtyPrompt?.kind === 'route') blocker.reset?.()
    setDirtyPrompt(null)
  }

  const handleToggleSite = useCallback((scheduleId: number) => { guardedAction(() => {
    if (view === 'calendar') {
      // 사용자가 직접 끈 현장(calInactiveSiteIds) 기준으로 토글 — 우측 패널 표시 로직과
      // 동일 기준을 써야 클릭했을 때 실제로 상태가 바뀐다. 예전에는 "현재 달에 태스크가
      // 있는지"(calActiveSiteIds)까지 같이 봐서, 해당 달에 태스크가 없는 현장은 클릭해도
      // 계속 "비활성" 판정이라 아무 반응이 없었다.
      if (calInactiveSiteIds.has(scheduleId)) {
        setCalInactiveSiteIds(prev => {
          const next = new Set(prev)
          next.delete(scheduleId)
          return next
        })
        const site = knownSites.find(s => s.scheduleId === scheduleId)
        if (site?.startDate && site?.endDate) {
          setCalJumpTo({ startDate: site.startDate, endDate: site.endDate })
        }
      } else {
        setCalInactiveSiteIds(prev => {
          const next = new Set(prev)
          next.add(scheduleId)
          return next
        })
      }
    } else {
      setSchedActiveSiteId(prev => prev === scheduleId ? prev : scheduleId)
    }
  }) }, [view, calInactiveSiteIds, knownSites, guardedAction])

  const selectedSite = knownSites.find(s => s.scheduleId === schedActiveSiteId)

  // create 플로우·설정은 우측 패널 숨김. 전송및공유이력(kakao-history)은 공용 조회카드가
  // 기간·현장 필터를 흡수하므로 이 화면도 우측 패널을 숨긴다.
  const hideRightPanel = view.startsWith('create-') || view === 'settings' || view === 'settings-alarm' || view === 'settings-duration' || view === 'settings-order' || view === 'settings-partner' || view === 'settings-foreman-confirm' || view === 'site-list' || view === 'assign-preview' || view === 'kakao-history'

  // 저장에 쓰는 생성 경로 — 상태(createOrigin)가 정본, 비어 있으면 단계로 추정(견적 전용 단계=QUOTE, 직접 입력 단계=MANUAL)
  const effectiveOrigin: OriginType | null =
    createOrigin ?? (view === 'create-extract-raw2' || view === 'create-extract2' ? 'QUOTE' : view === 'create-extract' ? 'MANUAL' : null)

  // 회사에 스케줄이 하나도 없음(조회 완료·오류 아님) — 간트 본문 C-0 안내와 우측 패널 CTA가 같은 판정을 쓴다
  const noSchedules = !allSitesLoading && !allSitesLoadError && allSites.length === 0

  const renderView = () => {
    switch (view) {
      case 'calendar':
        return (
          <CalendarMultiView
            navigate={navigate}
            inactiveSiteIds={inactiveSiteIds}
            todoSiteIds={calVisibleSiteIds}
            onSitesLoaded={handleSitesLoaded}
            jumpTo={calJumpTo}
            onJumpDone={() => setCalJumpTo(null)}
            onMonthChange={handleCalMonthChange}
          />
        )
      case 'gantt':          return <GanttView navigate={navigate} selectedSite={selectedSite} onTaskPanelChange={setGanttTaskPanelOpen} onDirtyChange={setDirty} deepLink={deepLink} onDeepLinkConsumed={() => setDeepLink(null)} noSchedules={noSchedules} />
      case 'cal-b2':         return <CalendarScheduleView navigate={navigate} selectedSite={selectedSite} onDirtyChange={setDirty} />
      case 'site-list':      return <SiteListView navigate={navigate} onSiteChanged={() => setSitesRefreshKey(k => k + 1)} />
      case 'create-upload':  return <UploadView navigate={navigate} onResult={r => { setExtractResult(r); if (entryMode !== 'QUOTE' && entryMode !== 'MANUAL') setCreateOrigin(r.skipped ? 'MANUAL' : 'QUOTE') }} resetSiteId={resetSiteId ?? undefined} entry={entryMode} />
      case 'create-extract': return <ExtractView navigate={navigate} flow="direct" originType={effectiveOrigin} extractResult={extractResult} onPreview={handleCreatePreview} snapshot={createDraft} onSnapshot={setCreateDraft} initYear={createInitMonth?.year} initMonth={createInitMonth?.month} selectedSiteId={selectedSiteId ?? undefined} resetSiteId={resetSiteId ?? undefined} onCreated={handleScheduleCreated} entry={entryMode} />
      // 현장 선택 — 직접·견적 공용(SiteSelectView).
      case 'create-site':    return <SiteSelectView navigate={navigate} onSiteSelected={handleSiteSelected} onDevSample={handleDevSample} entry={entryMode} />
      // 자동 생성(QUOTE) 전용 단계 — 업로드 → 추출 결과 확인 → 공종·공사일 (현장 선택은 위 create-site)
      // 업로드에서 새 파일을 고르면 추출 결과만 비운다(현장은 유지 — 현장이 업로드보다 앞 단계)
      case 'create-upload2':      return <UploadView2 navigate={navigate} onResult={setExtractResult} onRawResult={(r, f) => { setRawExtract(r); setRawFile(f ?? null) }} onReset={() => { setRawExtract(null); setExtractResult(null) }} selectedSiteId={resetSiteId ?? selectedSiteId ?? undefined} isResetFlow={resetSiteId != null} />
      case 'create-extract-raw2': return <ExtractRawView2 navigate={navigate} raw={rawExtract} choice={rawChoice} onChoice={setRawChoice} selectedSiteId={selectedSiteId ?? undefined} resetSiteId={resetSiteId ?? undefined} originType={effectiveOrigin} sourceFile={rawFile} onCreated={handleScheduleCreated} />
      case 'create-extract2': return <ExtractView navigate={navigate} flow="auto" originType={effectiveOrigin} extractResult={extractResult} onPreview={handleCreatePreview} snapshot={createDraft} onSnapshot={setCreateDraft} initYear={createInitMonth?.year} initMonth={createInitMonth?.month} selectedSiteId={selectedSiteId ?? undefined} resetSiteId={resetSiteId ?? undefined} onCreated={handleScheduleCreated} entry={entryMode} />
      case 'assign-preview': return <AssignPreviewView navigate={navigate} result={previewAssignResult} payload={createPayload} onCreated={handleScheduleCreated} />
      case 'kakao':
        return <KakaoShareView navigate={navigate} selectedSite={selectedSite} shareSendable={selectedSite ? shareSendableMap?.get(selectedSite.scheduleId) : undefined} />
      case 'kakao-history':
        // 전송및공유이력은 공용 ShareHistoryScreen 기반으로, 조회조건(기간·구분·열람상태·수신인
        // 검색·정렬)을 자체 조회카드에서 소유한다. 기존 우측 패널 날짜/현장 필터는 흡수되어
        // 이 화면에서는 우측 패널을 노출하지 않는다(아래 hideRightPanel).
        return <ShareHistoryView navigate={navigate} />
      case 'work-type':      return <ScheduleListView navigate={navigate} selectedSite={selectedSite} />
      case 'settings':
      case 'settings-alarm':   return <SettingsView navigate={navigate} />
      case 'settings-duration': return <DurationSettingsView navigate={navigate} />
      case 'settings-order':   return <OrderSettingsView navigate={navigate} onDirtyChange={setDirty} />
      case 'settings-partner': return <PartnerAssignView navigate={navigate} onDirtyChange={setDirty} />
      case 'settings-foreman-confirm': return <ForemanConfirmSettingsView navigate={navigate} />
      default:
        return (
          <CalendarMultiView
            navigate={navigate}
            inactiveSiteIds={inactiveSiteIds}
            todoSiteIds={calVisibleSiteIds}
            onSitesLoaded={handleSitesLoaded}
            jumpTo={calJumpTo}
            onJumpDone={() => setCalJumpTo(null)}
            onMonthChange={handleCalMonthChange}
          />
        )
    }
  }

  return (
    <CommonCodesProvider>
    <SiteRefreshProvider value={() => setSitesRefreshKey(k => k + 1)}>
      {/*
       * 공통 플랫폼 AppFrame 내부에서 렌더됨:
       *   - LeftNav (사이드바) → AppFrame 제공, Sidebar 컴포넌트 미사용
       *   - TopBar (상단 바)  → AppFrame 제공, 자체 Topbar 미사용
       *   - <main class="flex-1 overflow-auto"> → 이 컴포넌트가 h-full 로 채움
       */}
      <div className="flex h-full overflow-hidden font-sans antialiased" data-group="03">
        {/* 메인 콘텐츠 영역 */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* D-07 §21.4 — 이 래퍼는 16개 view 가 공유한다. 스케줄/캘린더/리스트는
              Page layout="fixed" 로 내부 스크롤을 소유해 여기를 넘치지 않지만,
              설정·업로드 등 문서형 뷰는 아직 이 래퍼의 스크롤에 의존하므로 overflow-auto 를 유지한다. */}
          <div className="flex-1 overflow-auto min-w-0 min-h-0">
            {renderView()}
          </div>

          {/* 우측 고객·현장 패널 — 간트 태스크 패널 열릴 때 숨김 */}
          {!hideRightPanel && (
            <div
              className="relative flex shrink-0"
              style={{
                overflow:      (view === 'gantt' && ganttTaskPanelOpen) ? 'hidden' : 'visible',
                maxWidth:      (view === 'gantt' && ganttTaskPanelOpen) ? 0 : undefined,
                opacity:       (view === 'gantt' && ganttTaskPanelOpen) ? 0 : 1,
                pointerEvents: (view === 'gantt' && ganttTaskPanelOpen) ? 'none' : undefined,
                transition:    'max-width .28s ease, opacity .22s ease',
              }}
            >
              <RightPanel
                sites={panelSites}
                // 인디플래너 홈은 panelSites 단계에서 이미 "해당 달과 겹치는 현장"만
                // 걸러왔으므로, 캘린더 좁은 조회창 기준 활성/비활성(calActiveSiteIds)이
                // 아니라 사용자가 직접 끈 현장(calInactiveSiteIds)만 비활성으로 표시한다.
                inactiveSiteIds={isCalendarView ? calInactiveSiteIds : inactiveSiteIds}
                onToggle={handleToggleSite}
                emptyMessage="현장이 없습니다"
                loading={allSitesLoading || scopeSites === undefined}
                loadError={(!isCalendarView && allSitesLoadError) || scopeLoadError}
                onRetry={() => setSitesRefreshKey(k => k + 1)}
                navigate={navigate}
                statusOrder={isCalendarView ? CAL_HOME_STATUS_ORDER : SCHED_PANEL_STATUS_ORDER}
                pinActiveFirst={!isCalendarView}
                onSiteChanged={() => setSitesRefreshKey(k => k + 1)}
                showCreateCta={view === 'gantt' || view === 'cal-b2' || view === 'work-type'}
                noSchedules={noSchedules}
                collapseWhenNoSchedules={view === 'gantt'}
                hideDoneChip
                assignee={{ values: memberIds, onChange: setMemberIds, members: companyMembers, myId }}
              />
            </div>
          )}
        </div>
      </div>

      {/* 저장 안 한 변경사항 확인 모달 */}
      <Modal
        open={dirtyPrompt != null}
        onClose={handleDirtyCancel}
        title={dirtyRef.current?.panel ? '저장하지 않은 변경이 있어요' : '저장하지 않은 변경사항이 있습니다'}
        footerAlign="stretch"
        footer={dirtyRef.current?.panel ? (
          <>
            <Button variant="secondary" onClick={handleDirtyCancel}>계속 편집</Button>
            <Button variant="danger" onClick={handleDirtyDiscard}>저장 안 하고 이동</Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              style={{ borderColor: 'var(--color-error)', color: 'var(--status-danger-600)' }}
              onClick={handleDirtyDiscard}
            >저장 안 함</Button>
            <Button variant="primary" loading={dirtySaving} onClick={handleDirtySave}>
              {dirtySaving ? '저장 중...' : '저장'}
            </Button>
          </>
        )}
      >
        {dirtyRef.current?.panel ? (
          <>
            <div style={DIRTY_BODY_STYLE}>
              공종 상세창에서 수정한 내용이 아직 저장되지 않았어요.
            </div>
          </>
        ) : (
          <>
            <div style={DIRTY_BODY_STYLE}>
              {dirtyRef.current?.subject ?? '날짜가 변경된 공종'}{dirtyRef.current ? ` ${dirtyRef.current.count}개` : ''}가 아직 저장되지 않았습니다. 저장하고 이동할까요?
            </div>
          </>
        )}
      </Modal>

      {/* 저장하고 이동한 뒤의 변경 카톡 확인 창 — 이동한 화면과 무관하게 부모에서 띄운다 */}
      {leaveNotice && (
        <ScheduleChangeNoticeModal
          scheduleId={leaveNotice.scheduleId}
          changes={leaveNotice.changes}
          onSent={() => {}}
          onClose={() => setLeaveNotice(null)}
        />
      )}
    </SiteRefreshProvider>
    </CommonCodesProvider>
  )
}